import axios from 'axios';
import notificationStore from '../services/notificationStore';
import { getActiveProjectId } from '../constants/projectConstants';

/**
 * Raise a header-bell notification for a schedule-intent change. City-tagged
 * (defaults to the active city) so the city-scoped feed shows it under the
 * right city. Best-effort — never throws.
 */
const notifyScheduleChange = ({ screenId, projectId, title, message, type = 'schedule-updated' } = {}) => {
  try {
    notificationStore.push({
      type,
      severity: 'info',
      title: title || 'Schedule updated',
      message: message || (screenId ? `Schedule changed for ${screenId}.` : 'A schedule was updated.'),
      deviceId: screenId || null,
      projectId: projectId || getActiveProjectId(),
    });
  } catch {
    /* notifications are non-critical */
  }
};

/**
 * schedulesApi — CRUD + read-projection client for the Sumadhura timeline
 * schedule-intent model (Phase 1/2 of the 2026-04-23 migration).
 *
 * Contract (authoritative — matches the staging backend at
 * https://api.staging.sumadhura.cms.convrse.ai):
 *
 *   POST   /api/sumadhura-cms/schedule-intents
 *          body { projectId, screenId, name, contentIds[], track: 1|2|3,
 *                 startTime: "HH:mm", endTime: "HH:mm",
 *                 overlapPolicy: "merge"|"replace",
 *                 startDate?, endDate?, daysOfWeek?, createdBy? }
 *   PATCH  /api/sumadhura-cms/schedule-intents/:intentId
 *          body { ... }   (all fields optional)
 *   DELETE /api/sumadhura-cms/schedule-intents/:intentId        (soft-cancel, 204)
 *   GET    /api/sumadhura-cms/screens/:screenId/schedule-intents?projectId=...
 *   GET    /api/sumadhura-cms/screens/:screenId/timeline?projectId=...&from=ISO&to=ISO
 *
 * Every handler takes `projectId` as its first arg (dashboard convention per
 * `project_media_projectid_required.md`). Server echoes camelCase `intentId`
 * alongside `id` on responses — callers should prefer `intentId`.
 */

const DEFAULT_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
export const CMS_API_BASE_URL =
  import.meta.env.VITE_CMS_API_BASE_URL || `${DEFAULT_API_BASE_URL}/api/sumadhura-cms`;

/**
 * Shared error extractor — mirrors the pattern in contentApi.js so the
 * dashboard surfaces readable messages instead of axios generic strings.
 */
const getErrorMessage = (error, fallback) => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    const messageStr = typeof data?.message === 'string' ? data.message : '';
    const detailStr = typeof data?.detail === 'string' ? data.detail : '';
    const looksLikeErrorCode = /^[A-Z][A-Z0-9_]*$/.test(messageStr.trim());
    if (looksLikeErrorCode && detailStr) return detailStr;
    const msg =
      messageStr ||
      detailStr ||
      (typeof data?.error === 'string' && data.error) ||
      error.message;
    return msg || fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
};

/**
 * List all active schedule intents for a screen.
 * Returns an array of intent rows (may be empty).
 * @param {string} projectId
 * @param {string} screenId
 * @returns {Promise<Array>}
 */
export const listScheduleIntents = async (projectId, screenId) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    if (!screenId) throw new Error('screenId is required');
    const response = await axios.get(
      `${CMS_API_BASE_URL}/screens/${encodeURIComponent(screenId)}/schedule-intents`,
      { params: { projectId } },
    );
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    return [];
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch schedule intents'));
  }
};

/**
 * Fetch the server-computed timeline window for a screen.
 * Segments are a read-only projection (not a stored table) — used for the
 * minimap bar on the list row and for verifying the local resolver preview.
 *
 * @param {string} projectId
 * @param {string} screenId
 * @param {string|Date|null} [from]  Optional window start (ISO). Default = backend "now".
 * @param {string|Date|null} [to]    Optional window end (ISO). Default = backend "now+24h".
 * @returns {Promise<{screenId,windowStart,windowEnd,model,segments:Array}>}
 */
export const getTimelineWindow = async (projectId, screenId, from = null, to = null) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    if (!screenId) throw new Error('screenId is required');
    const params = { projectId };
    if (from) params.from = from instanceof Date ? from.toISOString() : String(from);
    if (to) params.to = to instanceof Date ? to.toISOString() : String(to);
    const response = await axios.get(
      `${CMS_API_BASE_URL}/screens/${encodeURIComponent(screenId)}/timeline`,
      { params },
    );
    return response.data?.data || response.data || {};
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch timeline window'));
  }
};

/**
 * Create a schedule intent. Server assigns `intentId` / `id`.
 * @param {string} projectId
 * @param {Object} body
 * @returns {Promise<Object>} the created intent row
 */
export const createScheduleIntent = async (projectId, body) => {
  try {
    if (!projectId) throw new Error('projectId is required');
    const payload = { ...body, projectId };
    if (!payload.screenId) throw new Error('screenId is required');
    const response = await axios.post(`${CMS_API_BASE_URL}/schedule-intents`, payload);
    notifyScheduleChange({
      screenId: payload.screenId,
      projectId,
      message: `A new slot was scheduled on ${payload.screenId}.`,
    });
    return response.data?.data || response.data || {};
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create schedule intent'));
  }
};

/**
 * Patch one or more fields on an existing intent. All fields optional.
 * Returns the updated intent row.
 * @param {string} intentId
 * @param {Object} patch
 * @returns {Promise<Object>}
 */
export const updateScheduleIntent = async (intentId, patch) => {
  try {
    const id = String(intentId || '').trim();
    if (!id) throw new Error('intentId is required');
    const response = await axios.patch(
      `${CMS_API_BASE_URL}/schedule-intents/${encodeURIComponent(id)}`,
      patch || {},
    );
    const updated = response.data?.data || response.data || {};
    notifyScheduleChange({
      screenId: patch?.screenId || updated?.screenId || updated?.screen_id,
      projectId: patch?.projectId || updated?.projectId || updated?.project_id,
      message: 'A scheduled slot was updated.',
    });
    return updated;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update schedule intent'));
  }
};

/**
 * Adapter: create a schedule intent from the legacy createCmsSchedule shape.
 *
 * The intent model replaced the slot model — but several dashboard callers
 * (quick-schedule, SchedulerManifest, ScheduleEventModal) still build a slot-
 * shaped payload { projectId, screenId, contentId, startTime, endTime, date?
 * startDate?, endDate?, loopEnabled?, loopItems?, templateId?, zones? }.
 * This helper translates that shape into an intent write so callsites don't
 * have to rewrite body construction.
 *
 * Template/zones payloads (split-screen) are not yet supported by intents —
 * callers that rely on them must keep using the legacy createCmsSchedule
 * path until the intent schema gains zone support.
 *
 * @param {Object} legacyPayload
 * @param {string} legacyPayload.projectId
 * @param {string} legacyPayload.screenId
 * @param {string} legacyPayload.contentId
 * @param {string} legacyPayload.startTime
 * @param {string} legacyPayload.endTime
 * @param {string} [legacyPayload.date]
 * @param {string} [legacyPayload.startDate]
 * @param {string} [legacyPayload.endDate]
 * @param {boolean} [legacyPayload.loopEnabled]
 * @param {Array<{contentId:string}>} [legacyPayload.loopItems]
 * @param {string} [legacyPayload.name] Optional display name override.
 * @returns {Promise<Object>}
 */
export const createScheduleFromLegacyShape = async (legacyPayload) => {
  const p = legacyPayload || {};
  if (p.templateId || (Array.isArray(p.zones) && p.zones.length > 0)) {
    throw new Error(
      'Split-screen schedules (templateId/zones) are not yet supported by the intent API. Use the legacy createCmsSchedule path for zone payloads.',
    );
  }
  const contentIds = p.loopEnabled && Array.isArray(p.loopItems) && p.loopItems.length > 0
    ? p.loopItems.map((li) => String(li.contentId || '').trim()).filter(Boolean)
    : [String(p.contentId || '').trim()].filter(Boolean);
  if (contentIds.length === 0) {
    throw new Error('contentId (or non-empty loopItems) is required');
  }
  const body = {
    screenId: p.screenId,
    name: p.name || contentIds[0],
    contentIds,
    track: 1,
    startTime: p.startTime,
    endTime: p.endTime,
    overlapPolicy: 'merge',
  };
  if (p.startDate || p.date) body.startDate = p.startDate || p.date;
  if (p.endDate || p.date) body.endDate = p.endDate || p.date;
  // daysOfWeek dropped 2026-04-23 — a week is a week, no weekday filtering.
  return createScheduleIntent(p.projectId, body);
};

/**
 * Soft-cancel (delete) an intent. Backend returns 204.
 * @param {string} intentId
 * @returns {Promise<void>}
 */
export const deleteScheduleIntent = async (intentId) => {
  try {
    const id = String(intentId || '').trim();
    if (!id) throw new Error('intentId is required');
    await axios.delete(`${CMS_API_BASE_URL}/schedule-intents/${encodeURIComponent(id)}`);
    notifyScheduleChange({
      type: 'schedule-cancelled',
      title: 'Schedule slot cancelled',
      message: 'A scheduled slot was cancelled.',
    });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete schedule intent'));
  }
};
